/**
 * WorksheetCanvas - Drop zone for worksheet components
 *
 * Features:
 * - Droppable area for palette components
 * - Visual grid alignment
 * - Component selection
 * - Drag to reposition components
 * - Resize handles (future)
 */

import { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import {
  User,
  Type,
  FileText,
  Hash,
  Binary,
  Circle,
  Pencil,
  Calculator,
  BarChart2,
  Grid3X3,
  Move,
  Trash2,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { WorksheetComponent, WorksheetLayout } from "./types";

// ============================================================================
// Icon Mapper
// ============================================================================

const iconMap: Record<string, React.ElementType> = {
  name: User,
  text_write: Type,
  word_problem: FileText,
  number_blank: Hash,
  number_write: Binary,
  circle_options: Circle,
  draw_box: Pencil,
  number_sentence: Calculator,
  tally_marks: BarChart2,
  base_ten_blocks: Grid3X3,
};

// ============================================================================
// Canvas Component Item (Draggable + Selectable)
// ============================================================================

interface CanvasComponentProps {
  component: WorksheetComponent;
  isSelected: boolean;
  canvasWidth: number;
  canvasHeight: number;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}

function CanvasComponent({
  component,
  isSelected,
  canvasWidth,
  canvasHeight,
  onSelect,
  onDelete,
  onDuplicate,
}: CanvasComponentProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `canvas-${component.id}`,
      data: {
        type: "canvas-component",
        component,
      },
    });

  const Icon = iconMap[component.type] || FileText;

  // Calculate pixel position from percentage
  const left = component.position.x * canvasWidth;
  const top = component.position.y * canvasHeight;
  const width = component.position.width * canvasWidth;
  const height = component.position.height * canvasHeight;

  const style = {
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    zIndex: isDragging ? 1000 : isSelected ? 100 : 1,
  };

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(component.id);
    },
    [component.id, onSelect]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(component.id);
    },
    [component.id, onDelete]
  );

  const handleDuplicate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDuplicate(component.id);
    },
    [component.id, onDuplicate]
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleClick}
      className={cn(
        "absolute border-2 rounded-md bg-white transition-shadow cursor-pointer",
        "hover:shadow-md",
        isSelected
          ? "border-primary ring-2 ring-primary/20 shadow-lg"
          : "border-gray-200",
        isDragging && "opacity-70 shadow-xl"
      )}
    >
      {/* Component content */}
      <div className="h-full flex flex-col p-2 overflow-hidden">
        <div className="flex items-center gap-1.5 mb-1">
          <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs font-medium truncate">{component.label}</span>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 flex-1">
          {component.prompt}
        </p>
        {component.points !== undefined && component.points > 0 && (
          <div className="text-xs text-blue-600 mt-auto">
            {component.points} pt{component.points !== 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Selection controls */}
      {isSelected && (
        <>
          {/* Drag handle */}
          <div
            {...listeners}
            {...attributes}
            className={cn(
              "absolute -top-3 left-1/2 -translate-x-1/2",
              "bg-primary text-primary-foreground rounded px-2 py-0.5",
              "cursor-move flex items-center gap-1 text-xs"
            )}
          >
            <Move className="h-3 w-3" />
            Move
          </div>

          {/* Action buttons */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-6 w-6"
                  onClick={handleDuplicate}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Duplicate</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="destructive"
                  className="h-6 w-6"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Grid Overlay
// ============================================================================

interface GridOverlayProps {
  columns: number;
  rows: number;
  visible: boolean;
}

function GridOverlay({ columns, rows, visible }: GridOverlayProps) {
  if (!visible) return null;

  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Vertical lines */}
      {Array.from({ length: columns - 1 }).map((_, i) => (
        <div
          key={`v-${i}`}
          className="absolute top-0 bottom-0 border-l border-dashed border-gray-200"
          style={{ left: `${((i + 1) / columns) * 100}%` }}
        />
      ))}
      {/* Horizontal lines */}
      {Array.from({ length: rows - 1 }).map((_, i) => (
        <div
          key={`h-${i}`}
          className="absolute left-0 right-0 border-t border-dashed border-gray-200"
          style={{ top: `${((i + 1) / rows) * 100}%` }}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export interface WorksheetCanvasProps {
  components: WorksheetComponent[];
  layout: WorksheetLayout;
  selectedComponentId: string | null;
  showGrid?: boolean;
  onSelectComponent: (id: string | null) => void;
  onDeleteComponent: (id: string) => void;
  onDuplicateComponent: (id: string) => void;
  onUpdateComponentPosition: (
    id: string,
    position: { x: number; y: number }
  ) => void;
}

export function WorksheetCanvas({
  components,
  layout,
  selectedComponentId,
  showGrid = true,
  onSelectComponent,
  onDeleteComponent,
  onDuplicateComponent,
}: WorksheetCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Calculate canvas dimensions based on layout
  const aspectRatio = useMemo(() => {
    // Letter size ratio (8.5 x 11)
    const ratio = layout.orientation === "portrait" ? 11 / 8.5 : 8.5 / 11;
    return ratio;
  }, [layout.orientation]);

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (canvasRef.current) {
        const container = canvasRef.current.parentElement;
        if (container) {
          const availableWidth = container.clientWidth - 48; // padding
          const availableHeight = container.clientHeight - 48;

          // Calculate dimensions maintaining aspect ratio
          let width = availableWidth;
          let height = width * aspectRatio;

          if (height > availableHeight) {
            height = availableHeight;
            width = height / aspectRatio;
          }

          setDimensions({ width, height });
        }
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [aspectRatio]);

  // Droppable setup
  const { setNodeRef, isOver } = useDroppable({
    id: "worksheet-canvas",
  });

  // Handle click on canvas background (deselect)
  const handleCanvasClick = useCallback(() => {
    onSelectComponent(null);
  }, [onSelectComponent]);

  return (
    <div className="flex-1 flex items-center justify-center p-6 bg-muted/50 overflow-hidden">
      <div
        ref={(node) => {
          setNodeRef(node);
          (canvasRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node;
        }}
        onClick={handleCanvasClick}
        className={cn(
          "relative bg-white shadow-lg rounded-lg overflow-hidden",
          "transition-all duration-200",
          isOver && "ring-2 ring-primary ring-offset-2"
        )}
        style={{
          width: dimensions.width || "100%",
          height: dimensions.height || "auto",
          minWidth: 300,
          minHeight: 400,
        }}
      >
        {/* Page margin indicators */}
        <div
          className="absolute border border-dashed border-gray-300 pointer-events-none"
          style={{
            top: `${layout.margins.top * 50}px`,
            right: `${layout.margins.right * 50}px`,
            bottom: `${layout.margins.bottom * 50}px`,
            left: `${layout.margins.left * 50}px`,
          }}
        />

        {/* Grid overlay */}
        <GridOverlay columns={10} rows={14} visible={showGrid} />

        {/* Components */}
        {components.map((component) => (
          <CanvasComponent
            key={component.id}
            component={component}
            isSelected={selectedComponentId === component.id}
            canvasWidth={dimensions.width}
            canvasHeight={dimensions.height}
            onSelect={onSelectComponent}
            onDelete={onDeleteComponent}
            onDuplicate={onDuplicateComponent}
          />
        ))}

        {/* Empty state */}
        {components.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p className="text-sm">
                Drag components here to build your worksheet
              </p>
            </div>
          </div>
        )}

        {/* Drop indicator */}
        {isOver && (
          <div className="absolute inset-0 bg-primary/5 pointer-events-none flex items-center justify-center">
            <div className="bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg">
              Drop to add component
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
